/**
 * VSync instrument templates + config import.
 *
 * A Sahas VSync is a passive Modbus poller: it reads an instrument's holding
 * registers and watches ONE register (the param with `P_NO == 1`, a monotonic
 * cycle counter) to know when a fresh test result is ready. Adding a VSync to
 * the platform therefore means creating a Thing + one tag per polled register
 * (`name` = metric, `address` = register), which `config_push` then serves back
 * to the device.
 *
 * ⚠️ **Register addresses are per-instrument AND per-deployment.** A captured
 * real config (`bench-helper/backups/.../config.txt`) reads low registers
 * (45, 30, 42…), while the VEPL control-engine map (`machine_config.json`)
 * reads 4526/4656… for nominally the same instruments. There is **no** single
 * authoritative address catalogue. So the reliable path is to IMPORT the
 * device's own config (exact addresses via {@link parseVsyncConfig}); the
 * per-type {@link INSTRUMENT_HINTS} only supply stable metric *names* + types +
 * word-order (and a *typical* address to verify against the unit), never a
 * guaranteed address.
 */

import type { ThingTagInput, ThingDataType } from "@/lib/api/things";

/** Legacy VSync param type codes (Config.py / Data_Upload.py). */
export type VsyncParamType = 1 | 2 | 3 | 4; // 1,2 int · 3 long · 4 float

/** Map a VSync `P_TYPE` to our tag `data_type` facade. */
export function paramTypeToDataType(t: VsyncParamType): ThingDataType {
  switch (t) {
    case 4:
      return "number"; // float32
    case 3:
    case 2:
    case 1:
    default:
      return "integer";
  }
}

/** One param as it appears in a VSync `config.txt` / `send_config` response. */
export interface VsyncParam {
  P_ID: number;
  P_NO: number;
  P_PROTOCOL: number; // 1 = RTU (RS-485), 2 = TCP
  P_SLAVE_ID: number;
  P_IP_ADDR: string;
  P_REG_ADDR: number;
  P_REG_COUNT: number;
  P_TYPE: VsyncParamType;
}

export interface VsyncMachine {
  M_ID: number;
  M_PARAMETER_COUNT?: number;
  M_LAST_RECORDED_CYCLE?: number;
  M_PARAMETERS: VsyncParam[];
}

export interface VsyncConfig {
  DL_ID?: number;
  DL_MAC_ID?: string;
  DL_MACHINE_COUNT?: number;
  DL_MACHINES: VsyncMachine[];
}

/** One machine resolved from an imported config into platform tag rows. */
export interface ParsedVsyncMachine {
  mId: number;
  slaveId: number;
  transport: "modbus-rtu" | "modbus-tcp";
  ipAddr?: string;
  /** The register whose value is the monotonic cycle counter (P_NO == 1). */
  cycleCounterRegister: number | null;
  /** One tag per polled reading param (the cycle counter is excluded). */
  tags: ThingTagInput[];
}

/**
 * Parse a passive-VSync `config.txt` / `send_config` JSON body into per-machine
 * platform tag rows with the device's REAL register addresses. The `P_NO == 1`
 * cycle-counter param is recorded separately (it is the poll trigger, not a
 * stored reading) and excluded from the tags — matching what `Data_Upload`
 * uploads. Metric names are not in the wire config (only numeric `P_ID`s), so
 * each tag is named `param_<P_ID>` for the operator to rename (optionally
 * seeded from {@link INSTRUMENT_HINTS} at the call site).
 *
 * Throws on malformed JSON or a missing `DL_MACHINES` array so the caller can
 * surface a clear error.
 */
export function parseVsyncConfig(text: string): ParsedVsyncMachine[] {
  const cfg = JSON.parse(text) as VsyncConfig;
  if (!cfg || !Array.isArray(cfg.DL_MACHINES)) {
    throw new Error("Not a VSync config: missing DL_MACHINES array.");
  }
  return cfg.DL_MACHINES.map((m) => {
    const params = Array.isArray(m.M_PARAMETERS) ? m.M_PARAMETERS : [];
    const cycle = params.find((p) => p.P_NO === 1) ?? null;
    const first = params[0];
    const transport: "modbus-rtu" | "modbus-tcp" =
      (first?.P_PROTOCOL ?? 1) === 2 ? "modbus-tcp" : "modbus-rtu";
    const tags: ThingTagInput[] = params.map((p) => ({
      name: p.P_NO === 1 ? "cycle_count" : `param_${p.P_ID}`,
      data_type: paramTypeToDataType(p.P_TYPE),
      address: String(p.P_REG_ADDR),
      // Carry the exact VSync wire addressing so a re-import round-trips it.
      p_id: p.P_ID,
      p_type: p.P_TYPE,
      reg_count: p.P_REG_COUNT,
      // Include the P_NO==1 cycle counter as a MARKED tag (the device polls it as
      // the trigger; it is never published as a reading).
      is_cycle_counter: p.P_NO === 1,
    }));
    return {
      mId: m.M_ID,
      slaveId: first?.P_SLAVE_ID ?? 1,
      transport,
      ipAddr: first && transport === "modbus-tcp" ? first.P_IP_ADDR : undefined,
      cycleCounterRegister: cycle ? cycle.P_REG_ADDR : null,
      tags,
    };
  });
}

/**
 * Per-instrument metric-name hints. These carry the STABLE part of an
 * instrument (its metric names, types, word-order + a *typical* register to
 * verify), sourced from `docs/versatile/specs/ingestion-and-devices.md` §2.2
 * (VEPL `machine_config.json`). The `register` is a starting point only — the
 * operator confirms it against the unit, or imports the exact map via
 * {@link parseVsyncConfig}.
 */
export interface InstrumentHintTag {
  metric: string;
  /** Typical register — VERIFY against the unit; often differs per deployment. */
  typicalRegister: number;
  count: 1 | 2;
  paramType: VsyncParamType;
  wordOrder: "low_high" | "high_low";
  unit?: string;
  isCycleCounter?: boolean;
}

export interface InstrumentHint {
  id: string;
  label: string;
  description: string;
  defaultTransport: "modbus-rtu" | "modbus-tcp";
  tags: InstrumentHintTag[];
}

export const INSTRUMENT_HINTS: InstrumentHint[] = [
  {
    id: "vmbd",
    label: "VMBD — Methylene-Blue / Active-Clay",
    description:
      "Active-clay tester. Publishes active-clay %, MB solution, dot count; cycle_count is the poll trigger.",
    defaultTransport: "modbus-rtu",
    tags: [
      { metric: "active_clay_percent", typicalRegister: 4656, count: 2, paramType: 4, wordOrder: "low_high", unit: "%" },
      { metric: "mb_solution_ml", typicalRegister: 4608, count: 2, paramType: 4, wordOrder: "low_high", unit: "ml" },
      { metric: "dot_count", typicalRegister: 4596, count: 2, paramType: 3, wordOrder: "low_high" },
      { metric: "cycle_count", typicalRegister: 6120, count: 2, paramType: 3, wordOrder: "low_high", isCycleCounter: true },
    ],
  },
  {
    id: "vud",
    label: "VUD — Universal (permeability / strength)",
    description: "Single-result tester. Publishes one float32 result; verify the cycle-counter register on the unit.",
    defaultTransport: "modbus-rtu",
    tags: [
      { metric: "result", typicalRegister: 4526, count: 2, paramType: 4, wordOrder: "low_high" },
    ],
  },
  {
    id: "vccd",
    label: "VCCD — Compactability",
    description: "Compactability tester. Publishes one float32 result; verify the cycle-counter register on the unit.",
    defaultTransport: "modbus-rtu",
    tags: [
      { metric: "compactability_percent", typicalRegister: 4610, count: 2, paramType: 4, wordOrder: "low_high", unit: "%" },
    ],
  },
];

/** Look up a hint by id. */
export function instrumentHint(id: string): InstrumentHint | undefined {
  return INSTRUMENT_HINTS.find((h) => h.id === id);
}

/**
 * Turn an instrument hint into platform tag rows (name = metric, address =
 * typical register). The cycle-counter tag is INCLUDED as a marked
 * (`is_cycle_counter`) row — the device polls it as the `P_NO==1` trigger; it
 * simply never publishes a reading. Templates without one (VUD/VCCD) require the
 * operator to designate a cycle-counter row in the editor. Addresses are TYPICAL
 * — the operator verifies them.
 */
export function hintToTags(hint: InstrumentHint): ThingTagInput[] {
  return hint.tags.map((t) => ({
    name: t.metric,
    data_type: paramTypeToDataType(t.paramType),
    unit: t.unit,
    address: String(t.typicalRegister),
    // Carry the wire type + register count from the template (no P_ID — that
    // is per-deployment and only known from an actual config import).
    p_type: t.paramType,
    reg_count: t.count,
    is_cycle_counter: t.isCycleCounter,
  }));
}
